import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LifeBuoy, Plus, ChevronRight, Paperclip, MessagesSquare, Inbox } from "lucide-react";
import { SubHeader } from "@/components/Brand";
import { api, errMsg, useApi } from "@/lib/api";
import { fmtDateTime, rp } from "@/lib/format";
import { StatusBadge, StatusSteps, RelatedTx, Timeline, ReplyBox, toForm, KIND, REQ_STATUS } from "@/components/help/TicketParts";

const CAT_ORDER = ["pembayaran", "transfer", "pinjaman", "akun", "lainnya"];

export function HelpList() {
  const navigate = useNavigate();
  const { data } = useApi("/help/tickets");
  return (
    <div data-testid="help-center-page">
      <SubHeader title="Pusat Bantuan" onBack={() => navigate("/app")} />
      <div className="px-5 space-y-4">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-4 shadow-lg">
          <div className="flex items-center gap-3"><LifeBuoy className="h-8 w-8" /><div>
            <div className="font-bold">Laporkan kendala Anda</div>
            <div className="text-xs text-emerald-50">Setiap laporan mendapat nomor tiket. Pantau siapa yang menangani dan status penyelesaiannya.</div>
          </div></div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => navigate("/app/bantuan/baru")} data-testid="help-new-ticket" className="flex-1 flex items-center justify-center gap-1.5 bg-white keep-white font-bold text-sm py-2.5 rounded-xl" style={{ color: "#047857" }}><Plus className="h-4 w-4" />Buat Tiket</button>
            <button onClick={() => navigate("/app/chat")} data-testid="help-open-chat" className="flex items-center justify-center gap-1.5 bg-white/15 border border-white/30 font-semibold text-sm px-4 py-2.5 rounded-xl"><MessagesSquare className="h-4 w-4" />Chat Admin</button>
          </div>
        </div>
        <h2 className="font-bold text-slate-900">Tiket Saya</h2>
        {data?.length === 0 && (
          <div className="text-center py-8 text-slate-500 text-sm" data-testid="help-empty"><Inbox className="h-10 w-10 mx-auto text-slate-300 mb-2" />Belum ada tiket bantuan.</div>
        )}
        <div className="space-y-2">
          {data?.map((t) => (
            <button key={t.ticket_id} onClick={() => navigate(`/app/bantuan/${t.ticket_id}`)} data-testid={`help-ticket-${t.ticket_id}`}
              className="w-full text-left bg-white border border-slate-200 rounded-2xl p-3.5 flex items-center gap-3 hover:border-emerald-300 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-mono text-[11px] font-bold text-slate-500">{t.ticket_no}</span><StatusBadge status={t.status} label={t.status_label} /></div>
                <div className="font-semibold text-sm text-slate-900 truncate mt-1">{t.subject}</div>
                <div className="text-[11px] text-slate-500">{t.category_label} • {t.assigned_name ? `Ditangani ${t.assigned_name}` : "Belum ada petugas"} • {fmtDateTime(t.updated_at)}</div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HelpNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: meta } = useApi("/help/meta");
  const { data: reqs } = useApi("/requests");
  const [f, setF] = useState({ category: params.get("ref") ? "pembayaran" : "", subject: "", description: "", related_req_id: params.get("ref") || "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const mine = (reqs || []).filter((r) => !r.incoming).slice(0, 30);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/help/tickets", toForm(f, files));
      toast.success(`Tiket ${data.ticket_no} dibuat`);
      navigate(`/app/bantuan/${data.ticket_id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="help-new-page">
      <SubHeader title="Buat Tiket Bantuan" onBack={() => navigate("/app/bantuan")} />
      <div className="px-5 space-y-4">
        <div>
          <div className="text-sm font-semibold mb-2">Kategori</div>
          <div className="flex flex-wrap gap-2">
            {meta && CAT_ORDER.map((k) => (
              <button key={k} onClick={() => setF({ ...f, category: k })} data-testid={`help-cat-${k}`}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-colors ${f.category === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200"}`}>{meta.categories[k]}</button>
            ))}
          </div>
        </div>
        {["pembayaran", "transfer"].includes(f.category) && (
          <div>
            <div className="text-sm font-semibold mb-1">Transaksi terkait</div>
            <select value={f.related_req_id} onChange={(e) => setF({ ...f, related_req_id: e.target.value })} data-testid="help-related-select"
              className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm">
              <option value="">Pilih transaksi (opsional)</option>
              {mine.map((r) => <option key={r.req_id} value={r.req_id}>{fmtDateTime(r.created_at)} • {KIND[r.kind] || r.kind} • {rp(r.amount)} • {REQ_STATUS[r.status] || r.status}</option>)}
            </select>
          </div>
        )}
        <div>
          <div className="text-sm font-semibold mb-1">Judul kendala</div>
          <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} maxLength={120} placeholder="Contoh: Angsuran Oktober belum tercatat" data-testid="help-subject"
            className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm" />
        </div>
        <div>
          <div className="text-sm font-semibold mb-1">Jelaskan kendala</div>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={5} maxLength={3000} data-testid="help-description"
            placeholder="Tanggal bayar, nominal, metode pembayaran, dan apa yang terjadi" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 border border-dashed border-slate-300 rounded-xl px-3 py-3 cursor-pointer bg-white">
          <Paperclip className="h-4 w-4" /> {files.length ? `${files.length} lampiran dipilih` : "Lampirkan bukti bayar (foto/PDF, maks. 3)"}
          <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="help-files" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 3))} />
        </label>
        <button onClick={submit} disabled={busy || !f.category || f.subject.trim().length < 4 || f.description.trim().length < 10} data-testid="help-submit"
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl">{busy ? "Mengirim..." : "Kirim Tiket"}</button>
      </div>
    </div>
  );
}

export function HelpDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: t, reload } = useApi(`/help/tickets/${id}`);
  const { data: meta } = useApi("/help/meta");
  const reply = async (text, files) => {
    try {
      await api.post(`/help/tickets/${id}/reply`, toForm({ text }, files));
      toast.success("Tanggapan terkirim");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };
  if (!t || !meta) return <div className="p-8 text-center text-slate-500">Memuat...</div>;
  return (
    <div data-testid="help-detail-page">
      <SubHeader title={t.ticket_no} onBack={() => navigate("/app/bantuan")} />
      <div className="px-5 space-y-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0"><div className="text-[11px] text-slate-500">{t.category_label}</div><div className="font-bold text-slate-900" data-testid="help-detail-subject">{t.subject}</div></div>
            <StatusBadge status={t.status} label={t.status_label} testid="help-detail-status" />
          </div>
          <StatusSteps status={t.status} />
          <div className="text-xs text-slate-600" data-testid="help-detail-assignee">Ditangani oleh: <b className="text-slate-900">{t.assigned_name || "Menunggu petugas"}</b></div>
          <RelatedTx r={t.related} />
        </div>
        <Timeline t={t} statuses={meta.statuses} />
        <ReplyBox onSend={reply} placeholder={t.status === "selesai" ? "Masih ada kendala? Tulis untuk membuka kembali tiket" : "Tulis tanggapan atau kirim bukti tambahan"} />
      </div>
    </div>
  );
}
